import React, { useEffect, useState, useCallback } from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { useTranslator } from '@jutro/locale';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { PolicyChange} from 'gw-capability-policychange-common-react';
import { MLVeracityService, MLNavigationStepsTrackingService } from 'ml-services-common-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { platformMessages } from 'ml-components-platform-react';
import { MLDisclosureUtil, MLEndorsementFlowUtil } from 'ml-portals-util-js';
import appConfig from 'app-config';
import metadata from './MLPAVeracityCheckPage.metadata.json5';
import messages from './MLPAVeracityCheckPage.messages';

const MLPAVeracityCheckPage = (props) => {
    const {
        wizardData, goNext, currentStep, history, updateWizardData, changeNextSteps
    } = props;

    const { submissionVM } = wizardData;
    const isGpa = appConfig.portalCode === 'GPA';
    const { EndorsementService } = useDependencies('EndorsementService');
    const translator = useTranslator();
    const { authHeader } = useAuthentication();

    const [showVeracityFrame, setShowVeracityFrame] = useState(false);
    const [veracityURL, setVeracityURL] = useState('');


    const {
        showProgressDialog,
        showErrorModal
    } = MLModalService();

    const bindFlow = useCallback(async () => {
        const productCode = _.get(submissionVM, 'baseData.productCode.value', '');

        const canSkipSignatures = (MLNavigationStepsTrackingService.shouldQuickBind() && submissionVM.baseData.signatureRequired_ML.value === false);
        if (canSkipSignatures) {
            const bindPromise = EndorsementService.bindEndorsement(submissionVM.jobID.value, submissionVM.paymentDetails_ML.value, authHeader);
            showProgressDialog(platformMessages.savingPolicyChange, bindPromise);
            bindPromise.then(({ policyChange, ...rest }) => {
                submissionVM.value = new PolicyChange(policyChange, rest);
                updateWizardData(wizardData);
                // go to endorsementConfirmation
                MLEndorsementFlowUtil.findStepAndChangeNextStep(productCode, 'endorsementConfirmation', changeNextSteps);
                goNext();
            });
        } else if (isGpa) {
            const uwPromise = EndorsementService.evaluateUWPrebind(submissionVM.jobID.value, authHeader);
            showProgressDialog(platformMessages.savingPolicyChange, uwPromise);
            uwPromise.then((uwIssuesRes) => {
                submissionVM.value.uwIssues_ML = uwIssuesRes;
                updateWizardData(wizardData);
                MLEndorsementFlowUtil.findStepAndChangeNextStep(productCode, 'endorsementSignatureOptions', changeNextSteps);
                goNext();
            });
        } else {
            // temporary commenting out this as userType should be other for CE
            // else if (submissionVM.baseData.userType_ML.value && submissionVM.baseData.userType_ML.value.code === 'other' && !isGpa) {

            // go to endorsementESignature
            MLEndorsementFlowUtil.findStepAndChangeNextStep(productCode, 'endorsementESignature', changeNextSteps);
            goNext();
        }
    }, [EndorsementService, authHeader, changeNextSteps, goNext, isGpa, showProgressDialog, submissionVM, updateWizardData, wizardData]);

    const onNext = useCallback(() => {
        MLDisclosureUtil.setRequoteAfterMvr(submissionVM.jobID.value, false);

        _.set(submissionVM, 'portalCurrentStep_ML.value', 'endorsementPaVeracityCheck');
        const promise = EndorsementService.saveAndQuote(submissionVM.value, authHeader)
        showProgressDialog(platformMessages.quotingPolicyChange, promise);
        promise.then((responseData) => {
            MLTagManagerService.sendFormStepPolicyChangeStep();
            submissionVM.value = new PolicyChange(responseData);
            updateWizardData(wizardData);

            bindFlow();
        });
    }, [EndorsementService, authHeader, bindFlow, showProgressDialog, submissionVM, updateWizardData, wizardData]);

    const handleVeracityError = (error) => {
        console.error(error);
        const contactUsError = {
            customError: {
                quoteID: _.get(submissionVM, 'jobID.value'),
                accountNumber: _.get(submissionVM, 'accountNumber.value')
            }
        };
        // Running Promise.reject will throw us out of wizard and to contact us, passing in error state
        Promise.reject((contactUsError));
    };

    const getVeracityDetails = (callback) => {
        const request = {
            jobNumber: submissionVM.jobID.value
        };

        const promise = MLVeracityService.getVeracityDetailsByJobNumber(request, authHeader);
        showProgressDialog(platformMessages.loading, promise);
        promise.then(callback).catch((error) => {
            handleVeracityError(error);
        });
    };

    const saveSubmission = () => {
        _.set(submissionVM, 'portalCurrentStep_ML.value', 'endorsementPaVeracityCheck');
        const savePromise = EndorsementService.saveAndQuote(submissionVM.value, authHeader);
        showProgressDialog(platformMessages.loading, savePromise);
    };

    const exit = () => {
        MLDisclosureUtil.setRequoteAfterMvr(submissionVM.jobID.value, false);
        history.push('/');
    };

    const onSaveComplete = (data) => {
        if (data.status === 'save_return') {
            exit();
        } else if (data.status === 'complete' && data.recommendedAction === 'Decline') {
            // if recommendedAction is Decline, save the submission to update the alert and do not move from the screen
            saveSubmission();
        } else {
            onNext();
        }
    };

    const onError = () => {
        const title = translator(messages.errorTitle);
        const description = translator(messages.errorDescription);
        showErrorModal(title, description);
    };

    const handleVeracityEvent = (ev) => {
        if (ev.data.fromEvent === 'veracityIntegration') {
            const { data } = ev;
            if (data.action === 'onSaveComplete') {
                onSaveComplete(data.veracityData);
            } else if (data.action === 'onError') {
                onError(data.veracityData);
            } else {
                console.error(new Error('Unidentified Veracity interaction'));
            }
        }
    };

    const registerMessageEventListener = () => {
        window.addEventListener('message', handleVeracityEvent);
    };

    const destroyMessageEventListener = () => {
        window.removeEventListener('message', handleVeracityEvent);
    };

    const setVeracityDetails = (details) => {
        setVeracityURL(`/html/ml-veracity-iframe.html?tokenstring=${details.idResolveToken}&veracityIntegration=${details.idResolveUrl}`);
        setShowVeracityFrame(details.displayIdResolve);
    };

    const showLoadingWhileIframeIsLoaded = () => {
        const promise = new Promise((resolve) => {
            const maxWait = 5;
            let counter = 0;

            const intervalID = setInterval(() => {
                if (counter > maxWait) {
                    resolve();
                    window.clearInterval(intervalID);
                }
                const iframe = document.getElementById('veracityFrame')
                const idResolvePanel = iframe.contentWindow.document.getElementsByClassName('idresolveMainPanel')
                if (idResolvePanel.length > 0) {
                    resolve();
                    window.clearInterval(intervalID);
                }
                counter += 1;
            }, 2000);
        });
        showProgressDialog(platformMessages.loading, promise);
    };

    useEffect(() => {
        getVeracityDetails((data) => {
            if (data.displayIdResolve) {
                registerMessageEventListener();
                setVeracityDetails(data);
                showLoadingWhileIframeIsLoaded();
            } else {
                onNext(true);
            }
        });
        return () => {
            destroyMessageEventListener();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const overrides = {
        gpaPageHeaderContainer: {
            visible: isGpa
        },
        veracityFrame: {
            visible: showVeracityFrame,
            src: veracityURL
        }
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            showNext={false}
            policyChangePageHeaderML={currentStep.title}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
            />
        </WizardPage>
    );
};

MLPAVeracityCheckPage.propTypes = wizardProps;
export default withRouter(MLPAVeracityCheckPage);
